import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import {
  Heart,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  ExternalLink,
  Copy,
  DollarSign,
  Users,
  TrendingUp,
  Calendar,
  X,
  CheckCircle,
  AlertCircle,
  Home,
  ChevronRight,
  LogOut,
  RefreshCw
} from 'lucide-react';

const DonsPage = () => {
  const { user, logout, isReadOnly } = useAuth();
  const navigate = useNavigate();
  
  const [appels, setAppels] = useState([]);
  const [dons, setDons] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAppel, setSelectedAppel] = useState(null);
  const [currentView, setCurrentView] = useState('appels'); // 'appels' ou 'dons'
  
  // Modals
  const [showAppelModal, setShowAppelModal] = useState(false);
  const [showDonModal, setShowDonModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [editingAppel, setEditingAppel] = useState(null);
  
  // Filtres et recherche
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    type: 'all',
    year: ''
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Notifications
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  // Données pour les modals
  const [appelData, setAppelData] = useState({
    titre: '',
    description: '',
    objectif_montant: '',
    date_fin: '',
    type_don: 'general'
  });

  const [donData, setDonData] = useState({
    montant: '',
    donorType: 'member', // 'member', 'external', 'anonymous'
    user_id: '',
    nom_donateur: '',
    prenom_donateur: '',
    email_donateur: '',
    telephone_donateur: '',
    numero_whatsapp: '',
    objet_don: '',
    projet_soutenu: '',
    methode_paiement: 'mobile_money',
    anonyme: false,
    message_donateur: ''
  });

  // Liste des membres de la communauté
  const [communityMembers, setCommunityMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Helper pour afficher les notifications
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 4000);
  };

  // Helper pour formater les montants
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF'
    }).format(amount || 0);
  };

  // Charger les statistiques globales
  const loadStats = useCallback(async () => {
    try {
      const response = await apiService.get('/dons/stats');
      if (response && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  }, []);

  // Charger les appels à contribution
  const loadAppels = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.year) params.append('year', filters.year);
      
      const endpoint = `/dons/appels${params.toString() ? '?' + params.toString() : ''}`;
      const response = await apiService.get(endpoint);
      
      if (response && response.data) {
        setAppels(response.data || []);
      } else {
        console.error('Erreur lors du chargement des appels');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des appels:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Charger les dons d'un appel spécifique
  const loadDons = useCallback(async (appelId) => {
    if (!appelId) return;
    
    try {
      setLoading(true);
      const response = await apiService.get(`/dons/appels/${appelId}/dons`);
      
      if (response && response.data) {
        setDons(response.data || []);
      } else {
        console.error('Erreur lors du chargement des dons');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des dons:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Créer ou modifier un appel
  const handleSaveAppel = async (e) => {
    e.preventDefault();
    
    try {
      let response;
      if (editingAppel) {
        response = await apiService.put(`/dons/appels/${editingAppel.id}`, appelData);
      } else {
        response = await apiService.post('/dons/appels', appelData);
      }

      if (response && response.status === 'success') {
        showNotification('success', 
          editingAppel ? 'Appel modifié avec succès' : 'Appel créé avec succès'
        );
        
        if (!editingAppel && response.lien_don) {
          // Copier le lien automatiquement
          navigator.clipboard.writeText(response.lien_don);
          showNotification('info', 'Lien de don copié dans le presse-papiers !');
        }
        
        setShowAppelModal(false);
        setEditingAppel(null);
        setAppelData({
          titre: '',
          description: '',
          objectif_montant: '',
          date_fin: '',
          type_don: 'general'
        });
        loadAppels();
      } else {
        showNotification('error', response.message || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('error', 'Erreur lors de la sauvegarde');
    }
  };

  // Enregistrer un don
  const handleSaveDon = async (e) => {
    e.preventDefault();
    
    try {
      // Préparer les données selon le type de donateur
      const donPayload = {
        appel_don_id: selectedAppel.id,
        montant: donData.montant,
        methode_paiement: donData.methode_paiement,
        message_donateur: donData.message_donateur,
        anonyme: donData.donorType === 'anonymous',
        objet_don: donData.objet_don,
        projet_soutenu: donData.projet_soutenu,
        numero_whatsapp: donData.numero_whatsapp || donData.telephone_donateur,
      };

      if (donData.donorType === 'member' && donData.user_id) {
        donPayload.user_id = donData.user_id;
      } else if (donData.donorType === 'external') {
        donPayload.nom_donateur = donData.nom_donateur;
        donPayload.prenom_donateur = donData.prenom_donateur;
        donPayload.email_donateur = donData.email_donateur;
        donPayload.telephone_donateur = donData.telephone_donateur || donData.numero_whatsapp;
      }

      const response = await apiService.post('/dons', donPayload);

      if (response && response.status === 'success') {
        showNotification('success', 'Don enregistré avec succès');
        setShowDonModal(false);
        setDonData({
          montant: '',
          donorType: 'member',
          user_id: '',
          nom_donateur: '',
          prenom_donateur: '',
          email_donateur: '',
          telephone_donateur: '',
          numero_whatsapp: '',
          objet_don: '',
          projet_soutenu: '',
          methode_paiement: 'mobile_money',
          anonyme: false,
          message_donateur: ''
        });
        
        if (selectedAppel) {
          loadDons(selectedAppel.id);
        }
        loadStats();
      } else {
        showNotification('error', response.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('error', 'Erreur lors de l\'enregistrement');
    }
  };

  // Charger la liste des membres de la communauté
  const loadCommunityMembers = useCallback(async () => {
    try {
      setLoadingMembers(true);
      const response = await apiService.get('/dons/members');
      if (response && response.status === 'success') {
        setCommunityMembers(response.data || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des membres:', error);
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  // Supprimer un appel
  const handleDeleteAppel = async (appel) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'appel "${appel.titre}" ? Tous les dons associés seront également supprimés.`)) {
      return;
    }

    try {
      const response = await apiService.delete(`/dons/appels/${appel.id}`);

      if (response && response.status === 'success') {
        showNotification('success', 'Appel supprimé avec succès');
        loadAppels();
        loadStats();
      } else {
        showNotification('error', response.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('error', 'Erreur lors de la suppression');
    }
  };

  // Copier le lien de don
  const copyDonationLink = (appel) => {
    const link = `${window.location.origin}/don/${appel.token}`;
    navigator.clipboard.writeText(link);
    showNotification('success', 'Lien copié dans le presse-papiers !');
  };

  // Déconnexion
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Effects
  useEffect(() => {
    if (currentView === 'appels') {
      loadAppels();
    }
    loadStats();
  }, [currentView, loadAppels, loadStats]);

  useEffect(() => {
    if (selectedAppel) {
      loadDons(selectedAppel.id);
    }
  }, [selectedAppel, loadDons]);

  // Charger les membres quand on ouvre le modal de don
  useEffect(() => {
    if (showDonModal && communityMembers.length === 0) {
      loadCommunityMembers();
    }
  }, [showDonModal, communityMembers.length, loadCommunityMembers]);

  // Filtrage et pagination des appels
  const filteredAppels = appels.filter(appel => {
    if (filters.search && !appel.titre.toLowerCase().includes(filters.search.toLowerCase()) && 
        !appel.description.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.status !== 'all' && appel.status !== filters.status) {
      return false;
    }
    if (filters.type !== 'all' && appel.type_don !== filters.type) {
      return false;
    }
    return true;
  });

  const paginatedAppels = filteredAppels.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredAppels.length / itemsPerPage);

  // Helper pour les couleurs des types
  const getTypeColor = (type) => {
    const colors = {
      general: 'bg-blue-500',
      urgence: 'bg-red-500',
      projet: 'bg-green-500',
      evenement: 'bg-purple-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  // Helper pour les couleurs de statut
  const getStatusColor = (status) => {
    const colors = {
      actif: 'bg-green-100 text-green-800',
      suspendu: 'bg-yellow-100 text-yellow-800',
      termine: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-gradient-to-r from-blue-500 to-purple-600">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
            {/* Navigation breadcrumb */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1 sm:space-x-2 text-gray-600">
                <Home className="w-4 h-4" />
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="font-medium text-gray-900 text-sm sm:text-base">Dashboard</span>
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-purple-600 font-semibold text-sm sm:text-base">Gestion des Dons</span>
              </div>
            </div>

            {/* User info et actions */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-6">
              <div className="text-left sm:text-right order-2 sm:order-1">
                <p className="text-sm font-medium text-gray-900">
                  {user?.first_name || user?.name || 'Admin'} {user?.last_name || ''}
                </p>
                <p className="text-xs text-gray-500 flex items-center">
                  <Heart className="w-3 h-3 mr-1 text-red-500" />
                  <span className="hidden sm:inline">Fleurs de la Sainte Famille</span>
                  <span className="sm:hidden">FSF</span>
                </p>
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-4 order-1 sm:order-2">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center px-3 sm:px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 text-sm"
                >
                  <Home className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Retour au Dashboard</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="flex items-center px-3 sm:px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 text-sm"
                >
                  <LogOut className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Titre de la page */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Gestion des Dons FSF
          </h1>
          <p className="text-gray-600 text-base sm:text-lg">
            Créez des appels à contribution et suivez les dons reçus
          </p>
        </div>

        {/* Statistiques */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm sm:text-lg font-semibold text-gray-700">Appels Actifs</h3>
                  <p className="text-2xl sm:text-3xl font-bold text-blue-600">
                    {stats.stats_generales?.appels_actifs || 0}
                  </p>
                </div>
                <Heart className="w-8 h-8 sm:w-12 sm:h-12 text-blue-500" />
              </div>
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm sm:text-lg font-semibold text-gray-700">Total Dons</h3>
                  <p className="text-2xl sm:text-3xl font-bold text-green-600">
                    {stats.stats_generales?.total_dons || 0}
                  </p>
                </div>
                <Users className="w-8 h-8 sm:w-12 sm:h-12 text-green-500" />
              </div>
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border-l-4 border-yellow-500 col-span-2 lg:col-span-1">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm sm:text-lg font-semibold text-gray-700">Montant Total</h3>
                  <p className="text-lg sm:text-2xl font-bold text-yellow-600">
                    {formatCurrency(stats.stats_generales?.montant_total || 0)}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 sm:w-12 sm:h-12 text-yellow-500" />
              </div>
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border-l-4 border-purple-500 col-span-2 lg:col-span-1">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm sm:text-lg font-semibold text-gray-700">Ce Mois</h3>
                  <p className="text-lg sm:text-2xl font-bold text-purple-600">
                    {formatCurrency(stats.stats_generales?.montant_30_derniers_jours || 0)}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 sm:w-12 sm:h-12 text-purple-500" />
              </div>
            </div>
          </div>
        )}

        {/* Navigation des vues */}
        <div className="bg-white rounded-xl shadow-lg mb-6 sm:mb-8">
          <div className="border-b border-gray-200 px-4 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
              <div className="flex space-x-1">
                <button
                  onClick={() => {
                    setCurrentView('appels');
                    setSelectedAppel(null);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentView === 'appels'
                      ? 'bg-blue-500 text-white'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <Heart className="w-4 h-4 inline mr-2" />
                  Appels à Contribution
                </button>
                
                {selectedAppel && (
                  <button
                    onClick={() => setCurrentView('dons')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      currentView === 'dons'
                        ? 'bg-green-500 text-white'
                        : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                    }`}
                  >
                    <DollarSign className="w-4 h-4 inline mr-2" />
                    Dons - {selectedAppel.titre}
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {!isReadOnly && currentView === 'appels' && (
                  <button
                    onClick={() => {
                      setEditingAppel(null);
                      setAppelData({
                        titre: '',
                        description: '',
                        objectif_montant: '',
                        date_fin: '',
                        type_don: 'general'
                      });
                      setShowAppelModal(true);
                    }}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvel Appel
                  </button>
                )}
                
                {!isReadOnly && currentView === 'dons' && selectedAppel && (
                  <button
                    onClick={() => setShowDonModal(true)}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Enregistrer Don
                  </button>
                )}
                
                <button
                  onClick={() => {
                    if (currentView === 'appels') {
                      loadAppels();
                    } else if (selectedAppel) {
                      loadDons(selectedAppel.id);
                    }
                  }}
                  className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Actualiser</span>
                </button>
              </div>
            </div>
          </div>

          {/* Filtres */}
          {currentView === 'appels' && (
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rechercher
                  </label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Titre ou description..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="actif">Actif</option>
                    <option value="suspendu">Suspendu</option>
                    <option value="termine">Terminé</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="all">Tous les types</option>
                    <option value="general">Général</option>
                    <option value="urgence">Urgence</option>
                    <option value="projet">Projet</option>
                    <option value="evenement">Événement</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Année
                  </label>
                  <select
                    value={filters.year}
                    onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">Toutes les années</option>
                    {[2030, 2029, 2028, 2027, 2026, 2025].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Contenu principal */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        ) : currentView === 'appels' ? (
          /* Liste des appels */
          <div>
            {paginatedAppels.length === 0 ? (
              <div className="text-center py-12">
                <Heart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">Aucun appel à contribution trouvé</p>
                {!isReadOnly && (
                <button
                  onClick={() => {
                    setEditingAppel(null);
                    setAppelData({
                      titre: '',
                      description: '',
                      objectif_montant: '',
                      date_fin: '',
                      type_don: 'general'
                    });
                    setShowAppelModal(true);
                  }}
                  className="mt-4 flex items-center mx-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Créer votre premier appel
                </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {paginatedAppels.map((appel) => {
                    const pourcentage = appel.objectif_montant > 0 
                      ? Math.min((appel.total_collecte / appel.objectif_montant) * 100, 100)
                      : 0;

                    return (
                      <div key={appel.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                        {/* Header de la carte */}
                        <div className="p-6 border-b border-gray-100">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <div className={`w-3 h-3 rounded-full ${getTypeColor(appel.type_don)}`}></div>
                                <span className="text-xs font-medium text-gray-500 uppercase">
                                  {appel.type_don}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appel.status)}`}>
                                  {appel.status}
                                </span>
                              </div>
                              <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">
                                {appel.titre}
                              </h3>
                              <p className="text-gray-600 text-sm line-clamp-3">
                                {appel.description}
                              </p>
                            </div>
                          </div>

                          {/* Statistiques de l'appel */}
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="text-center">
                              <p className="text-2xl font-bold text-green-600">
                                {appel.total_dons || 0}
                              </p>
                              <p className="text-xs text-gray-500">Don{(appel.total_dons || 0) > 1 ? 's' : ''}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-blue-600">
                                {formatCurrency(appel.total_collecte || 0).replace(' XOF', '')}
                              </p>
                              <p className="text-xs text-gray-500">Collecté</p>
                            </div>
                          </div>

                          {/* Barre de progression */}
                          {appel.objectif_montant > 0 && (
                            <div className="mb-4">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-xs text-gray-500">Progression</span>
                                <span className="text-xs font-medium text-gray-700">{pourcentage.toFixed(1)}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${pourcentage}%` }}
                                ></div>
                              </div>
                              <div className="flex justify-between items-center mt-1">
                                <span className="text-xs text-gray-500">
                                  {formatCurrency(appel.total_collecte || 0)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatCurrency(appel.objectif_montant)}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Date de fin */}
                          {appel.date_fin && (
                            <div className="flex items-center text-xs text-gray-500 mb-4">
                              <Calendar className="w-3 h-3 mr-1" />
                              Fin le {new Date(appel.date_fin).toLocaleDateString()}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="p-6 bg-gray-50">
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            <button
                              onClick={() => {
                                setSelectedAppel(appel);
                                setCurrentView('dons');
                              }}
                              className="flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Voir Dons
                            </button>
                            
                            <button
                              onClick={() => copyDonationLink(appel)}
                              className="flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Copier Lien
                            </button>
                          </div>
                          
                          {!isReadOnly && (
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => {
                                setEditingAppel(appel);
                                setAppelData({
                                  titre: appel.titre,
                                  description: appel.description,
                                  objectif_montant: appel.objectif_montant || '',
                                  date_fin: appel.date_fin || '',
                                  type_don: appel.type_don
                                });
                                setShowAppelModal(true);
                              }}
                              className="flex items-center justify-center px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Modifier
                            </button>
                            
                            <button
                              onClick={() => handleDeleteAppel(appel)}
                              className="flex items-center justify-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Supprimer
                            </button>
                          </div>
                          )}
                          
                          <a
                            href={`/don/${appel.token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-full px-3 py-2 mt-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Page Publique
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Précédent
                    </button>
                    
                    <span className="px-4 py-2 text-gray-700">
                      Page {currentPage} sur {totalPages}
                    </span>
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Suivant
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          /* Liste des dons */
          <div>
            {selectedAppel && (
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedAppel.titre}</h3>
                    <p className="text-gray-600 mb-4">{selectedAppel.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600">Total des dons</p>
                        <p className="text-2xl font-bold text-blue-600">{selectedAppel.total_dons || 0}</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600">Montant collecté</p>
                        <p className="text-2xl font-bold text-green-600">
                          {formatCurrency(selectedAppel.total_collecte || 0)}
                        </p>
                      </div>
                      {selectedAppel.objectif_montant > 0 && (
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Progression</p>
                          <p className="text-2xl font-bold text-purple-600">
                            {Math.min(((selectedAppel.total_collecte || 0) / selectedAppel.objectif_montant) * 100, 100).toFixed(1)}%
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      setSelectedAppel(null);
                      setCurrentView('appels');
                    }}
                    className="ml-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Liste des dons */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-bold text-gray-900">
                  Liste des Dons ({dons.length})
                </h3>
              </div>

              {dons.length === 0 ? (
                <div className="text-center py-12">
                  <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucun don enregistré pour cet appel</p>
                  {!isReadOnly && (
                  <button
                    onClick={() => setShowDonModal(true)}
                    className="mt-4 flex items-center mx-auto px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Enregistrer le premier don
                  </button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto text-justify">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Donateur</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Montant</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Méthode</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Message</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {dons.map((don) => (
                        <tr key={don.id} className="hover:bg-gray-50 transition-colors align-top">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900">
                                {don.anonyme ? 'Donateur anonyme' : `${don.prenom_donateur || ''} ${don.nom_donateur || ''}`.trim() || don.nom_donateur || '—'}
                              </p>
                              {don.email_donateur && (
                                <p className="text-sm text-gray-500">{don.email_donateur}</p>
                              )}
                              {(don.numero_whatsapp || don.telephone_donateur) && (
                                <p className="text-sm text-gray-500">{don.numero_whatsapp || don.telephone_donateur}</p>
                              )}
                              {don.projet_soutenu && (
                                <p className="text-sm text-amber-700 mt-1">
                                  <span className="font-medium">Projet :</span> {don.projet_soutenu}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="font-semibold text-green-600">{formatCurrency(don.montant)}</p>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                              {don.methode_paiement}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-600">
                              {new Date(don.created_at).toLocaleDateString()}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-600 max-w-xs">
                              {don.message_donateur || '-'}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal pour créer/modifier un appel */}
      {showAppelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">
                    {editingAppel ? 'Modifier l\'Appel' : 'Nouvel Appel à Contribution'}
                  </h2>
                  <p className="text-blue-100 mt-1">
                    {editingAppel ? 'Modifiez les informations' : 'Créez un lien de don personnalisé'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAppelModal(false);
                    setEditingAppel(null);
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSaveAppel} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Titre de l'appel *
                </label>
                <input
                  type="text"
                  required
                  value={appelData.titre}
                  onChange={(e) => setAppelData({ ...appelData, titre: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                  placeholder="Ex: Soutien pour la construction de l'église..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  required
                  rows={4}
                  value={appelData.description}
                  onChange={(e) => setAppelData({ ...appelData, description: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all resize-none"
                  placeholder="Décrivez le but de cet appel à contribution..."
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Objectif (optionnel)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={appelData.objectif_montant}
                    onChange={(e) => setAppelData({ ...appelData, objectif_montant: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="Montant souhaité en XOF"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Date de fin (optionnelle)
                  </label>
                  <input
                    type="date"
                    value={appelData.date_fin}
                    onChange={(e) => setAppelData({ ...appelData, date_fin: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Type d'appel *
                </label>
                <select
                  required
                  value={appelData.type_don}
                  onChange={(e) => setAppelData({ ...appelData, type_don: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                >
                  <option value="general">Général</option>
                  <option value="urgence">Urgence</option>
                  <option value="projet">Projet spécifique</option>
                  <option value="evenement">Événement</option>
                </select>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAppelModal(false);
                    setEditingAppel(null);
                  }}
                  className="px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  {editingAppel ? (
                    <>
                      <Edit className="w-4 h-4 mr-2" />
                      Modifier
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Créer l'Appel
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal pour enregistrer un don */}
      {showDonModal && selectedAppel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Enregistrer un Don</h2>
                  <p className="text-green-100 mt-1">Pour : {selectedAppel.titre}</p>
                </div>
                <button
                  onClick={() => setShowDonModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSaveDon} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Montant du don * (en XOF)
                </label>
                <input
                  type="number"
                  required
                  min="100"
                  value={donData.montant}
                  onChange={(e) => setDonData({ ...donData, montant: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
                  placeholder="Ex: 50000"
                />
              </div>

              {/* Type de donateur */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Type de donateur *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setDonData({ ...donData, donorType: 'member', anonyme: false })}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      donData.donorType === 'member'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Users className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm font-medium">Membre FSF</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setDonData({ ...donData, donorType: 'external', anonyme: false, user_id: '' })}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      donData.donorType === 'external'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <DollarSign className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm font-medium">Donateur externe</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setDonData({ ...donData, donorType: 'anonymous', anonyme: true, user_id: '', nom_donateur: '' })}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      donData.donorType === 'anonymous'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Eye className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm font-medium">Anonyme</span>
                  </button>
                </div>
              </div>

              {/* Sélection d'un membre */}
              {donData.donorType === 'member' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Sélectionner un membre *
                  </label>
                  {loadingMembers ? (
                    <div className="flex items-center justify-center p-4 border-2 border-dashed border-gray-200 rounded-lg">
                      <RefreshCw className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                      <span className="text-gray-500">Chargement des membres...</span>
                    </div>
                  ) : (
                    <select
                      required
                      value={donData.user_id}
                      onChange={(e) => setDonData({ ...donData, user_id: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
                    >
                      <option value="">-- Choisir un membre --</option>
                      {communityMembers.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name} {member.couple_name ? `(${member.couple_name})` : ''} - {member.email}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Informations donateur externe */}
              {donData.donorType === 'external' && (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nom du donateur *
                      </label>
                      <input
                        type="text"
                        required
                        value={donData.nom_donateur}
                        onChange={(e) => setDonData({ ...donData, nom_donateur: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
                        placeholder="Dupont"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Prénom du donateur *
                      </label>
                      <input
                        type="text"
                        required
                        value={donData.prenom_donateur}
                        onChange={(e) => setDonData({ ...donData, prenom_donateur: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
                        placeholder="Jean"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={donData.email_donateur}
                        onChange={(e) => setDonData({ ...donData, email_donateur: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
                        placeholder="jean@exemple.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        WhatsApp *
                      </label>
                      <input
                        type="tel"
                        required
                        value={donData.numero_whatsapp}
                        onChange={(e) => setDonData({ ...donData, numero_whatsapp: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
                        placeholder="+229 XX XX XX XX"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Objet du don
                    </label>
                    <input
                      type="text"
                      value={donData.objet_don}
                      onChange={(e) => setDonData({ ...donData, objet_don: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
                      placeholder="Ex: Soutien au centre communautaire"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Projet à soutenir
                    </label>
                    <input
                      type="text"
                      value={donData.projet_soutenu}
                      onChange={(e) => setDonData({ ...donData, projet_soutenu: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
                      placeholder="Ex: Construction du centre communautaire"
                    />
                  </div>
                </>
              )}

              {/* Message pour don anonyme */}
              {donData.donorType === 'anonymous' && (
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <Eye className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                  <p className="text-sm text-purple-700">
                    Ce don sera enregistré de façon anonyme. Aucune information personnelle ne sera associée.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Méthode de paiement *
                </label>
                <select
                  required
                  value={donData.methode_paiement}
                  onChange={(e) => setDonData({ ...donData, methode_paiement: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
                >
                  <option value="mobile_money">Mobile Money</option>
                  <option value="carte">Carte bancaire</option>
                  <option value="especes">Espèces</option>
                  <option value="virement">Virement bancaire</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Message du donateur (optionnel)
                </label>
                <textarea
                  rows={3}
                  value={donData.message_donateur}
                  onChange={(e) => setDonData({ ...donData, message_donateur: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all resize-none"
                  placeholder="Message d'encouragement ou intention du don..."
                />
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowDonModal(false)}
                  className="px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Enregistrer le Don
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-auto p-4 rounded-lg shadow-lg z-50 flex items-center space-x-2 ${
          notification.type === 'success' ? 'bg-green-500 text-white' : 
          notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
        }`}>
          {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span className="text-sm sm:text-base">{notification.message}</span>
        </div>
      )}
    </div>
  );
};

export default DonsPage;